// ═══ 업무별 기록 v5 — ERP 에 이미 있는 건(겹침) 정하기 (DESIGN_V5 §2.5·§6.4) ═══
// 같은 업체·같은 번호판이 ERP 장부(2026-08 까지)에 이미 있는 엑셀 기록을 골라
//   '엑셀 건 빼기'(exclude — ERP 것만 남김) / '별건으로 넣기'(separate — 둘 다 장부에) / '선택 해제'(clear) 로 정한다.
// 서버 계약:
//   GET  /work-records/cards/:balanceId/preview → preview.overlap:[{recordId, srcRow, plate, unitId, erpItemId, erpMonth, erpJobKind, erpSubtotal, excelAmount, choice}]
//   POST /work-records/cards/:balanceId/overlap {recordIds:[1..500], choice, memo(1..200)} → {success, updated, skipped:[{id, reason}]} · 409 CARD_POSTED
// G4(카드 밖 행) 는 gap 으로 연다 — 같은 본문·응답:
//   GET  /work-records/gaps/:gapId/preview?choice=post → preview.run.overlap (같은 모양) · POST /work-records/gaps/:gapId/overlap
//        409 CARD_POSTED = 'post' 결정이 살아 있음 (결정 취소 뒤 가능)
//
// props: card | gap · showToast · onClose · onChanged
import React, { useState, useEffect } from 'react';
import { is } from './SettlementItemDrawer';
import { fmt, num, signed, signColor, getJson, postJson, codeMessage, overlay, box, th, thR, td, tdR, noteBox, errBoxSt } from './RecordsPreviewModal';

var MAX_IDS = 500;
var CHOICE_TEXT = { '': '정하지 않음', exclude: '엑셀 건 빼기', separate: '별건으로 넣기' };
var CHOICES = [['exclude', '엑셀 건 빼기', 'ERP 장부에 있는 건만 남기고 이 엑셀 건은 넣지 않습니다.'],
  ['separate', '별건으로 넣기', '다른 일이라 엑셀 건도 장부에 넣습니다.'], ['clear', '선택 해제', '정하지 않은 상태로 되돌립니다.']];

export default function RecordsOverlapModal(props) {
  var card = props.card || {}, gap = props.gap;
  var base = gap ? '/work-records/gaps/' + gap.gapId : '/work-records/cards/' + card.balanceId;
  var showToast = props.showToast || function () {};
  var [rows, setRows] = useState(null);
  var [names, setNames] = useState({});
  var [loadErr, setLoadErr] = useState('');
  var [reloadN, setReloadN] = useState(0);
  var [sel, setSel] = useState({});
  var [choice, setChoice] = useState('');
  var [memo, setMemo] = useState('');
  var [notice, setNotice] = useState('');
  var [busy, setBusy] = useState(false);

  useEffect(function () {
    var alive = true;
    setLoadErr('');
    getJson(base + (gap ? '/preview?choice=post' : '/preview')).then(function (res) {
      if (!alive) return;
      var p = gap ? (res.data.preview && res.data.preview.run) : res.data.preview;
      if (!res.data.success || !p) { setLoadErr(res.data.code ? codeMessage(res.data.code, res.data) : (res.data.error || '조회 실패')); setRows([]); return; }
      var nm = {};
      (p.byUnit || []).concat(gap ? gap.rows || [] : []).forEach(function (u) { if (u.unitName) nm[u.unitId] = u.unitName; });
      setNames(nm); setRows(p.overlap || []);
    }).catch(function () { if (alive) { setLoadErr('서버 오류'); setRows([]); } });
    return function () { alive = false; };
  }, [base, gap, reloadN]);

  var list = rows || [];
  var ids = Object.keys(sel).map(function (k) { return parseInt(k, 10); });
  var canRun = !busy && ids.length > 0 && ids.length <= MAX_IDS && !!choice && memo.trim().length > 0;

  var run = function () {
    if (!canRun) return;
    setBusy(true); setNotice('');
    postJson(base + '/overlap', { recordIds: ids, choice: choice, memo: memo.trim() }).then(function (res) {
      setBusy(false);
      var d = res.data;
      if (!d.success) { setNotice(gap && d.code === 'CARD_POSTED' ? '이미 장부에 반영된 항목이라 바꿀 수 없습니다. [결정 취소]를 누른 뒤 다시 정해 주세요.' : codeMessage(d.code, d)); return; }
      var skipped = (d.skipped || []).length;
      showToast(fmt(d.updated) + '건을 \'' + (CHOICES.filter(function (c) { return c[0] === choice; })[0] || [])[1] + '\'(으)로 정했습니다' + (skipped ? ' · ' + skipped + '건은 변경 없음' : ''));
      setSel({}); setChoice(''); setMemo('');
      setReloadN(function (n) { return n + 1; });
      if (props.onChanged) props.onChanged();
    }).catch(function () { setBusy(false); setNotice('서버 오류 — 잠시 뒤 다시 눌러 주세요.'); });
  };

  var title = (gap ? gap.no : card.no) + ' ERP 에 이미 있는 건 정하기';
  return (<div style={overlay} onClick={function () { if (!busy) props.onClose(); }}>
    <div role="dialog" aria-label={title} onClick={function (e) { e.stopPropagation(); }} style={box}>
      <h3 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: '#1B3A5C' }}>{title}</h3>
      <p style={{ margin: '0 0 12px', fontSize: 14, color: '#475569', lineHeight: 1.6 }}>같은 업체·같은 차량번호가 ERP 장부에 이미 있습니다. 건을 고른 뒤 어떻게 할지 정해 주세요.</p>
      {!!loadErr && <div style={errBoxSt}>{loadErr}</div>}
      {rows === null && <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>불러오는 중…</div>}
      {!!rows && !loadErr && !list.length && <div style={{ padding: 20, textAlign: 'center', color: '#64748b', fontSize: 14 }}>ERP 에 이미 있는 건이 없습니다.</div>}
      {list.length > 0 && <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 8 }}>
        <table style={{ width: '100%', minWidth: 680, borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}><input type="checkbox" aria-label="전체 선택" checked={list.every(function (o) { return sel[o.recordId]; })}
              onChange={function (e) { var on = e.target.checked; var n = {}; if (on) list.forEach(function (o) { n[o.recordId] = true; }); setSel(n); }} /></th>
            <th style={th}>엑셀 행</th><th style={th}>차량번호</th><th style={th}>업체</th><th style={th}>ERP 달·업무</th>
            <th style={thR}>ERP 금액</th><th style={thR}>엑셀 금액</th><th style={thR}>차이</th><th style={th}>지금</th>
          </tr></thead>
          <tbody>{list.map(function (o) {
            var diff = num(o.excelAmount) - num(o.erpSubtotal);
            return (<tr key={o.recordId} data-testid="overlap-row">
              <td style={td}><input type="checkbox" aria-label={'선택 ' + o.srcRow + '행'} checked={!!sel[o.recordId]}
                onChange={function () { setSel(function (p) { var n = Object.assign({}, p); if (n[o.recordId]) delete n[o.recordId]; else n[o.recordId] = true; return n; }); }} /></td>
              <td style={td}>{o.srcRow}행</td><td style={Object.assign({}, td, { fontWeight: 700 })}>{o.plate || '-'}</td><td style={td}>{names[o.unitId] || o.unitId}</td>
              <td style={td}>{o.erpMonth} · {o.erpJobKind}</td><td style={tdR}>{fmt(o.erpSubtotal)}</td><td style={tdR}>{fmt(o.excelAmount)}</td>
              <td style={Object.assign({}, tdR, { color: signColor(diff) })}>{diff ? signed(diff) : '-'}</td>
              <td style={Object.assign({}, td, { fontWeight: 700, color: o.choice ? '#047857' : '#b45309' })}>{CHOICE_TEXT[o.choice || ''] || o.choice}</td>
            </tr>);
          })}</tbody>
        </table>
      </div>}

      {list.length > 0 && <div>
        <div style={{ marginTop: 14, fontSize: 14, fontWeight: 700, color: '#334155' }}>고른 {fmt(ids.length)}건을</div>
        <div role="radiogroup" aria-label="겹친 건 처리" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
          {CHOICES.map(function (c) {
            var on = choice === c[0];
            return (<button key={c[0]} type="button" role="radio" aria-checked={on} aria-label={c[1]} onClick={function () { setChoice(c[0]); }}
              style={{ flex: 1, minWidth: 180, textAlign: 'left', padding: '10px 12px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit',
                border: '2px solid ' + (on ? '#2563EB' : '#e2e8f0'), background: on ? '#eff6ff' : '#fff' }}>
              <div style={{ fontSize: 14.5, fontWeight: 800, color: on ? '#1d4ed8' : '#334155' }}>{on ? '● ' : '○ '}{c[1]}</div>
              <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 3 }}>{c[2]}</div>
            </button>);
          })}
        </div>
        <label style={{ display: 'block', marginTop: 12, fontSize: 13.5, fontWeight: 700, color: '#475569' }}>메모 (꼭 적어 주세요)
          <input aria-label="메모" value={memo} maxLength={200} onChange={function (e) { setMemo(e.target.value); }} style={Object.assign({}, is, { marginTop: 4, fontSize: 14 })} />
        </label>
        {ids.length > MAX_IDS && <div style={noteBox}>한 번에 {MAX_IDS}건까지 정할 수 있습니다.</div>}
      </div>}
      {!!notice && <div style={noteBox} data-testid="overlap-notice">{notice}</div>}

      <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
        <button className="btn btn-outline" style={{ flex: 1, justifyContent: 'center', minHeight: 44, fontSize: 15 }} disabled={busy} onClick={props.onClose}>닫기</button>
        {list.length > 0 && <button className="btn btn-blue" style={Object.assign({ flex: 1, justifyContent: 'center', minHeight: 44, fontSize: 15 }, canRun ? null : { opacity: 0.45, cursor: 'not-allowed' })}
          disabled={!canRun} onClick={run}>{busy ? '처리 중…' : '정하기'}</button>}
      </div>
    </div>
  </div>);
}
